import { useCallback, useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Building2, Check, Loader2, Plus } from "lucide-react";
import BillingInvoices from "@app/components/BillingInvoices";
import OrganisationIdentity from "@app/components/organisation/OrganisationIdentity";
import OrganisationMembers from "@app/components/organisation/OrganisationMembers";
import {
  isOrganisationDetail,
  isOrganisationsResponse,
  type OrganisationDetail,
  type OrganisationRole,
  type OrganisationsResponse,
  type OrganisationType,
} from "@app/components/organisation/types";
import { useOrganisationMessages } from "@app/components/organisation/useOrganisationMessages";
import { Badge } from "@app/components/ui/badge";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { cn } from "@app/lib/utils";
import API from "@app/services/api";
import { isDemoEmail } from "@app/utils/demo-account";
import { workIn } from "@app/utils/organisation-context";
import { refreshUser } from "@app/utils/use-most-fresh-user";
import useUser from "@app/zustand/user";

/**
 * Configuration → Organisation (PT-119): the organisations the account belongs to, the one it works
 * in, the invitations waiting for it — then the organisation on screen, its members and invoices.
 *
 * An organisation is who pays and who manages the accounts. It grants no access to any dossier:
 * working for an industriel narrows what the account reads, it never widens it.
 *
 * The demo shows a fictitious one, as its gestionnaire sees it and with no control: the server
 * refuses every change it would make.
 */
export default function OrganisationSettings() {
  const { t } = useLingui();
  const readOnly = isDemoEmail(
    useUser((state) => (state.user as { email?: string } | null)?.email),
  );
  const { failureMessage } = useOrganisationMessages();
  const [loading, setLoading] = useState(true);
  const [loadResult, setLoadResult] = useState<unknown>(null);
  const [overview, setOverview] = useState<OrganisationsResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrganisationDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  const typeLabel = (type: OrganisationType) =>
    type === "industriel"
      ? t`Industriel`
      : type === "editeur"
        ? t`Éditeur`
        : t`Soins`;
  const roleLabel = (role: OrganisationRole) =>
    role === "gestionnaire" ? t`Gestionnaire` : t`Membre`;

  /** The list again; ``select`` names the organisation to show, else the one on screen, the active one, the first. */
  const loadOverview = useCallback((select?: string | null) => {
    API.get({ path: "/organisation" })
      .then((res: unknown) => {
        setLoadResult(res);
        if (!isOrganisationsResponse(res)) return;
        setOverview(res);
        setSelectedId((current) => {
          const ids = res.organisations.map((o) => o.id);
          const wanted = select ?? current;
          if (wanted && ids.includes(wanted)) return wanted;
          return (
            (res.organisations.find((o) => o.active) ?? res.organisations[0])
              ?.id ?? null
          );
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => loadOverview(), [loadOverview]);

  useEffect(() => {
    setDetail(null);
    if (!selectedId) return;
    API.get({ path: `/organisation/${selectedId}` }).then((res: unknown) => {
      if (isOrganisationDetail(res)) setDetail(res);
    });
  }, [selectedId]);

  function answer(key: string, request: Promise<unknown>) {
    setBusy(key);
    setError("");
    request
      .then((res: unknown) => {
        if (isOrganisationsResponse(res)) {
          setOverview(res);
          loadOverview();
        } else {
          setError(failureMessage(res, t`L'action a échoué. Réessayez.`));
        }
      })
      .finally(() => setBusy(null));
  }

  function switchTo(organisationId: string | null) {
    setBusy(`switch-${organisationId ?? "aucune"}`);
    setError("");
    workIn(organisationId).then((res) => {
      // On success the page reloads; only a refusal comes back here.
      if (!isOrganisationsResponse(res)) {
        setError(failureMessage(res, t`L'action a échoué. Réessayez.`));
        setBusy(null);
      }
    });
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-slate-500">
            <Trans>Chargement...</Trans>
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!overview) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-red-600" role="alert">
            {failureMessage(
              loadResult,
              t`Impossible de charger vos organisations.`,
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  const active = overview.organisations.find((o) => o.active) ?? null;

  return (
    <div className="space-y-4">
      {overview.invitations.length > 0 && (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle>
              <Trans>Invitations reçues</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {overview.invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm"
              >
                <div className="space-y-1">
                  <p className="text-slate-900">
                    <Trans>
                      {invitation.invited_by} vous invite à rejoindre{" "}
                      <strong>{invitation.raison_sociale}</strong> (
                      {roleLabel(invitation.role)}).
                    </Trans>
                  </p>
                  {invitation.type === "industriel" && (
                    <p className="text-xs text-slate-500">
                      <Trans>
                        Organisation industrielle : en y travaillant, vous ne
                        consultez que le catalogue et les données agrégées de sa
                        spécialité, jamais vos patients. Accepter ne change rien
                        tant que vous ne choisissez pas d'y travailler.
                      </Trans>
                    </p>
                  )}
                </div>
                <div className={cn("flex gap-2", readOnly && "hidden")}>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy !== null}
                    onClick={() =>
                      answer(
                        `decline-${invitation.id}`,
                        API.post({
                          path: `/organisation/invitations/${invitation.id}/decline`,
                        }),
                      )
                    }
                  >
                    <Trans>Refuser</Trans>
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy !== null}
                    onClick={() =>
                      answer(
                        `accept-${invitation.id}`,
                        API.post({
                          path: `/organisation/invitations/${invitation.id}/accept`,
                        }),
                      )
                    }
                  >
                    <Trans>Accepter</Trans>
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <Trans>Mes organisations</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>
              Une organisation regroupe des comptes et reçoit leurs factures :
              votre cabinet, votre établissement, ou un industriel pour lequel
              vous travaillez. Elle ne donne accès à aucun dossier patient.
            </Trans>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {overview.organisations.length === 0 && (
            <p className="text-sm text-slate-600">
              <Trans>
                Vous n'appartenez à aucune organisation. Créez celle de votre
                cabinet ou de votre établissement, ou acceptez une invitation.
              </Trans>
            </p>
          )}
          <ul className="space-y-2">
            {overview.organisations.map((organisation) => (
              <li
                key={organisation.id}
                className={cn(
                  "flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm",
                  organisation.id === selectedId
                    ? "border-primary/50 bg-primary/5"
                    : "border-slate-200",
                )}
              >
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-2 text-left"
                  onClick={() => {
                    setCreating(false);
                    setSelectedId(organisation.id);
                  }}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="truncate font-medium text-slate-900">
                    {organisation.raison_sociale}
                  </span>
                  <Badge variant="outline">
                    {typeLabel(organisation.type)}
                  </Badge>
                  <span className="text-slate-500">
                    {roleLabel(organisation.role)}
                  </span>
                </button>
                {organisation.active ? (
                  <Badge>
                    <Check className="mr-1 h-3 w-3" />
                    <Trans>Vous y travaillez</Trans>
                  </Badge>
                ) : readOnly ? null : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy !== null}
                    onClick={() => switchTo(organisation.id)}
                  >
                    {busy === `switch-${organisation.id}` && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <Trans>Travailler dans cette organisation</Trans>
                  </Button>
                )}
              </li>
            ))}
          </ul>

          {active?.narrows_session && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">
              <span>
                <Trans>
                  Vous travaillez pour {active.raison_sociale} : vous consultez
                  le catalogue et les données agrégées de sa spécialité, sans
                  accès à vos patients.
                </Trans>
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={busy !== null}
                onClick={() => switchTo(null)}
              >
                <Trans>Travailler hors organisation</Trans>
              </Button>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          {readOnly && (
            <p className="text-xs text-slate-500">
              <Trans>
                Organisation fictive de démonstration : rien n'y est modifiable.
              </Trans>
            </p>
          )}

          {!creating && !readOnly && (
            <div className="border-t pt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setCreating(true);
                  setSelectedId(null);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                <Trans>Créer une organisation</Trans>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {creating && (
        <OrganisationIdentity
          organisation={null}
          role={null}
          onSaved={(created) => {
            setCreating(false);
            loadOverview(created.organisation.id);
          }}
          onCancel={() => {
            setCreating(false);
            loadOverview();
          }}
        />
      )}

      {!creating && detail && (
        <>
          {/* MAGE-X's own organisation is never invoiced: no billing identity to show. */}
          {detail.organisation.type !== "editeur" && (
            <OrganisationIdentity
              organisation={detail.organisation}
              role={readOnly ? "membre" : detail.role}
              onSaved={(saved) => {
                setDetail(saved);
                loadOverview(saved.organisation.id);
              }}
            />
          )}
          <OrganisationMembers
            detail={detail}
            readOnly={readOnly}
            onChanged={setDetail}
            onLeft={() => {
              // Leaving the organisation the account works in leaves it in none: the session changes.
              if (
                overview.organisations.find((o) => o.id === selectedId)?.active
              ) {
                refreshUser("organisation-left").then(() =>
                  window.location.reload(),
                );
                return;
              }
              setSelectedId(null);
              loadOverview(null);
            }}
          />
          {detail.role === "gestionnaire" &&
            detail.organisation.type !== "editeur" && (
              <BillingInvoices organisationId={detail.organisation.id} />
            )}
        </>
      )}
    </div>
  );
}
